import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Mail, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../utils/api';

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (user && user !== false) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(formData.email, formData.password);
      toast.success('Welcome back, Admin!');
      navigate('/admin/dashboard');
    } catch (error) {
      const errorMsg = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0f0f10] via-[#1a1a1a] to-[#2d1f1f] px-6 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#8B1538] rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#D4AF37] rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md"
      >
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <motion.div
            whileHover={{ scale: 1.05, rotate: 5 }}
            className="w-20 h-20 bg-gradient-to-br from-[#8B1538] to-[#A52A2A] rounded-sm flex items-center justify-center border-2 border-[#D4AF37]"
          >
            <Shield className="w-10 h-10 text-[#D4AF37]" />
          </motion.div>
        </div>

        {/* Card */}
        <div className="bg-gradient-to-br from-[#1a1a1a] to-[#2d1f1f] p-8 md:p-12 rounded-sm border border-[#D4AF37]/20 backdrop-blur-xl">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">
              Admin <span className="text-[#D4AF37]">Portal</span>
            </h1>
            <p className="text-sm text-gray-400">INDOAXIUM SOFTWARE SERVICES</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#D4AF37]/50" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="admin-email-input"
                  className="bg-[#0f0f10] border-[#D4AF37]/20 text-white focus:border-[#D4AF37] pl-11 h-12"
                  placeholder="admin@indoaxium.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#D4AF37]/50" />
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="admin-password-input"
                  className="bg-[#0f0f10] border-[#D4AF37]/20 text-white focus:border-[#D4AF37] pl-11 h-12"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                type="submit"
                disabled={isLoading}
                data-testid="admin-login-submit"
                className="w-full bg-gradient-to-r from-[#D4AF37] to-[#F4E4B0] text-[#1a1a1a] hover:shadow-lg hover:shadow-[#D4AF37]/30 transition-all duration-300 py-6 text-lg font-semibold disabled:opacity-50"
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </motion.div>
          </form>

          <div className="mt-6 pt-6 border-t border-[#D4AF37]/10">
            <a href="/" className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors duration-200 flex items-center justify-center gap-2">
              ← Back to Website
            </a>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
